import { useState } from "react";
import { Car, Check, ChevronLeft, Hotel, Lock, MapPin, Plane, X } from "lucide-react";
import { Icon, MDisplay, Spinner } from "@cntrl/ui";
import { useLiveGroup } from "../../app/realtime/RealtimeProvider.js";
import { RunOfShowClock } from "../mobile/RunOfShowClock.js";
import { RunOfShowList } from "../mobile/DayOfShowDetail.js";
import {
  formatShowDate,
  futureShowHeadline,
  isoDateOffset,
  listCardDate,
  parseTimeToMinutes,
  showDayDelta,
} from "../mobile/dayOfShow.js";
import dos from "../mobile/dayOfShow.module.scss";
import { useMe } from "../../app/hooks/useSession.js";
import { usePartnerShow, usePartnerView, type PartnerTravelRow } from "../../app/hooks/usePartner.js";
import { eventLabel, eventTimeLabel, partnerFlights, shortDate, stayingLabel, t12, tspan } from "./partnerView.js";
import { JourneyStrip } from "../mobile/JourneyStrip.js";
import styles from "./PartnerDetailScreen.module.scss";

export type PartnerDetailProps =
  | { kind: "show"; id: string; onBack: () => void }
  | { kind: "event"; id: string; onBack: () => void };

/** "11:40 AM" from a wall-clock date-time ("2026-10-09T11:40:00") — the leg's own local time, never shifted. */
const wallTime = (dt: string | null | undefined): string => (dt ? t12(dt.slice(11, 19)) : "");

/**
 * One item, opened (ART-239 part 2) — a ROUTE, not an overlay, so the back swipe and browser Back work and no dialog
 * is hand-rolled. A show is THE MEMBER's BAND'S DAY OF SHOW at `/dos?show=` (product owner, 2026-10-08): the same live
 * clock and run of show the member's screen renders, without the buttons up top — so the partner can see they are in
 * soundcheck and not call. The partner's own guest status sits above it; where the member is staying and how they are
 * getting there below. An event: when and
 * where. The on-the-list celebration is a view of this page, display-only.
 */
export function PartnerDetailScreen(props: PartnerDetailProps) {
  return props.kind === "show" ? <ShowDetail id={props.id} onBack={props.onBack} /> : <EventDetail id={props.id} onBack={props.onBack} />;
}

function Bar({ onBack }: { onBack: () => void }) {
  return (
    <div className={styles.bar}>
      <button type="button" className={styles.back} onClick={onBack}>
        <Icon icon={ChevronLeft} size={16} /> Back
      </button>
    </div>
  );
}

function ShowDetail({ id, onBack }: { id: string; onBack: () => void }) {
  // The same live group the band's Day of Show listens on: a run-of-show or set-time edit refetches the partner's screen
  // the moment it lands. The server admits them to the BASE group only (never #guests / #money).
  useLiveGroup(`show:${id}`);
  const show = usePartnerShow(id);
  const view = usePartnerView();
  const me = useMe();
  const [celebrating, setCelebrating] = useState(false);
  const member = view.data?.member;
  const memberFirst = member?.firstName ?? null;
  const multiBand = (view.data?.bandCount ?? 0) > 1;

  if (show.isLoading) {
    return (
      <div className={dos.center} role="status" aria-live="polite">
        <Spinner size={22} label="Loading day of show" />
      </div>
    );
  }
  if (show.isError) {
    return (
      <DosFrame>
        <DosHeader onBack={onBack} title="Day of Show" />
        <p className={dos.empty} role="alert">Couldn&rsquo;t load this show.</p>
      </DosFrame>
    );
  }
  const d = show.data;
  if (!d) {
    return (
      <DosFrame>
        <DosHeader onBack={onBack} title="Day of Show" />
        <p className={dos.empty}>
          {memberFirst ? <>This show isn&rsquo;t on {memberFirst}&rsquo;s calendar any more.</> : <>This show isn&rsquo;t on the calendar any more.</>}
        </p>
      </DosFrame>
    );
  }

  const date = shortDate(d.date ?? "");
  const plus = d.plusCount ?? 0;

  if (celebrating && d.onList) {
    return (
      <div className={styles.onlist}>
        <button type="button" className={styles.onlistClose} aria-label="Close" onClick={() => setCelebrating(false)}>
          <Icon icon={X} size={20} />
        </button>
        <div className={styles.onlistCheck} aria-hidden="true"><Icon icon={Check} size={32} /></div>
        <p className={styles.onlistK}>{d.title ?? "Show"} · {date}</p>
        <h1 className={styles.onlistH}>You&rsquo;re on<br />the list</h1>
        <p className={styles.onlistP}>
          {memberFirst ?? "They"} added you. You&rsquo;re all set, just bring your ID to the box office.
        </p>
        <div className={styles.onlistBoxes}>
          <div className={styles.onlistBox}>
            <div className={styles.onlistBk}>Name</div>
            <div className={styles.onlistBv}>{me.data?.person?.firstName || "You"}</div>
          </div>
          {plus > 0 && (
            <div className={styles.onlistBox}>
              <div className={styles.onlistBk}>Guests</div>
              <div className={styles.onlistBv}>+{plus}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // The band's Day of Show, minus the buttons up top (product owner): the live clock and the run of show are the
  // same components the member's screen renders, over the same rows, so the partner can see they are in soundcheck without asking.
  const items = d.runOfShow ?? [];
  const hasTimeline = items.some((i) => parseTimeToMinutes(i.startsAt) != null);
  const todayIso = isoDateOffset(0);
  const dayDelta = showDayDelta(d.date, todayIso);
  const venueCityLine = [d.title, d.city].filter(Boolean).join(" · ");
  const travel = d.travel ?? [];
  const hotels = travel.filter((t) => t.kind === "hotel");
  // Flights ride the crew's live journey strip; a drive keeps its plain card.
  const flights = partnerFlights(travel);
  const drives = travel.filter((t) => t.kind !== "hotel" && t.kind !== "flight");

  return (
    <DosFrame testId="partner-dos">
      <DosHeader onBack={onBack} title={[d.title || "Day of Show", formatShowDate(d.date)].filter(Boolean).join(" · ")} />
      {(multiBand || d.confirmed === false) && (
        <p className={dos.empty}>
          {[multiBand ? d.bandName : null, d.confirmed === false ? "Not locked in yet — this date can still change" : null]
            .filter(Boolean)
            .join(" · ")}
        </p>
      )}

      {d.onList ? (
        <button type="button" className={styles.gcard} data-on="true" onClick={() => setCelebrating(true)}>
          <span className={styles.gcardRow}>
            <span className={styles.gcardIc} aria-hidden="true"><Icon icon={Check} size={20} /></span>
            <span>
              <span className={styles.gcardT}>You&rsquo;re on the list</span>
              <span className={styles.gcardS}>
                {me.data?.person?.firstName || "You"}{plus > 0 ? ` · +${plus} guest${plus === 1 ? "" : "s"}` : ""}
              </span>
            </span>
          </span>
          <span className={styles.gcardNote}>
            <Icon icon={Lock} size={16} />
            <span>You only see <b>your own</b> status, never who else is on the list.</span>
          </span>
        </button>
      ) : null}

      {dayDelta > 0 ? (
        <div className={dos.now} data-mode="future" data-testid="dos-future-hero">
          <div className={dos.nowHead}>
            <span className={dos.nowEyebrow}>{listCardDate(d.date, todayIso)}</span>
          </div>
          <div className={dos.nowTitle}>{futureShowHeadline(dayDelta)}</div>
          <div className={dos.nowSub}>{venueCityLine}</div>
        </div>
      ) : hasTimeline ? (
        <RunOfShowClock items={items} timezone={d.timezone} dayDelta={dayDelta} />
      ) : (
        <div className={dos.now} data-mode="wrapped">
          <div className={dos.nowEyebrow}>Run of show</div>
          <div className={dos.nowTitle}>Not set yet</div>
        </div>
      )}

      <RunOfShowList
        items={items}
        timezone={d.timezone}
        ourSlotId={d.mySlotId ?? null}
        mineTag={(memberFirst ?? "Them").toUpperCase()}
      />

      {hotels.map((h) => (
        <section key={h.id} className={dos.card}>
          <h2 className={`${dos.sectionEyebrow} ${styles.dosCardHead}`}>
            <Icon icon={Hotel} size={16} /> {stayingLabel(memberFirst)}
          </h2>
          <div className={styles.cardT}>{h.propertyName ?? "Hotel"}</div>
          {h.address && <div className={styles.dosCardD}>{h.address}</div>}
          {h.checkIn && <div className={styles.dosCardD}>Check-in {shortDate(h.checkIn)}</div>}
        </section>
      ))}

      {/* The flight strip IS a card — no outer card or "Getting there" heading around it (PO). */}
      {flights.legs.length > 0 && (
        <section aria-label="Flights">
          <JourneyStrip legs={flights.legs} statusById={flights.statusById} />
        </section>
      )}
      {drives.map((m) => <MoveCard key={m.id} leg={m} />)}
    </DosFrame>
  );
}

/** The band DOS page frame, plus the notch inset the partner frame (which has no app header) leaves to the page. */
function DosFrame({ children, testId }: { children: React.ReactNode; testId?: string }) {
  return (
    <div className={styles.dosTop}>
      <div className={dos.screen} data-testid={testId}>{children}</div>
    </div>
  );
}

/** The band DOS header — back, eyebrow, title — with no action buttons (product owner: "not the buttons up top"). */
function DosHeader({ onBack, title }: { onBack: () => void; title: string }) {
  return (
    <header className={dos.header}>
      <button type="button" className={dos.headerBack} onClick={onBack} aria-label="Back">
        <Icon icon={ChevronLeft} size={20} />
      </button>
      <div className={dos.headerText}>
        <span className={dos.headerEyebrow}>Day of Show</span>
        <MDisplay className={dos.headerTitle}>{title}</MDisplay>
      </div>
    </header>
  );
}

function MoveCard({ leg }: { leg: PartnerTravelRow }) {
  const flight = leg.kind === "flight";
  const route = [leg.departAirport, leg.arriveAirport].filter(Boolean).join(" → ");
  const depart = wallTime(leg.departLocal);
  return (
    <section className={dos.card}>
      <h2 className={`${dos.sectionEyebrow} ${styles.dosCardHead}`}>
        <Icon icon={flight ? Plane : Car} size={16} /> Getting there
      </h2>
      <div className={styles.cardT}>
        {flight ? [leg.airline, leg.flightNumber].filter(Boolean).join(" ") || "Flight" : "Drive"}
      </div>
      {route && <div className={styles.dosCardD}>{route}</div>}
      {depart && <div className={styles.dosCardD}>Departs {depart}</div>}
    </section>
  );
}

function EventDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const view = usePartnerView();
  const item = view.data?.items?.find((i) => i.id === id && i.kind === "event");

  if (view.isLoading) return <div className={styles.app}><Bar onBack={onBack} /><p className={styles.state} role="status">Loading…</p></div>;
  if (!item) {
    return <div className={styles.app}><Bar onBack={onBack} /><p className={styles.state}>This isn&rsquo;t on the calendar any more.</p></div>;
  }
  const when = tspan(item.startTime, item.endTime);
  return (
    <div className={styles.app}>
      <Bar onBack={onBack} />
      <div className={styles.body}>
        <p className={styles.k}>{eventLabel(item.eventType)} · {shortDate(item.date ?? "")}</p>
        <h1 className={styles.v}>{item.title ?? eventLabel(item.eventType)}</h1>
        {item.locationName && <p className={styles.c}><Icon icon={MapPin} size={16} /> {item.locationName}</p>}
        {when && (
          <section className={styles.card}>
            <h2 className={styles.cardK}>When</h2>
            <div className={styles.tlRow}>
              <span className={styles.tlDot} aria-hidden="true" />
              <span className={styles.tlTime}>{when}</span>
              <span className={styles.tlLab}>{eventTimeLabel(item.eventType)}</span>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
